import { useEffect, useSyncExternalStore } from 'react';
import { signInAnonymously, User } from 'firebase/auth';
import { auth } from 'config/initAuth';
import { useAuthContext } from 'context/AuthContext';
import { graphQLFetcher } from 'api/fetchers';
import {
  PackageFavouriteHotel,
  PackageFavouriteHotelsResult,
  packageFavouriteHotelSetQuery,
  packageFavouriteHotelsQuery,
} from 'api/queries/packageTour';
import { authHeaders } from 'utils/authHeaders';
import { withFavourite } from 'utils/favouriteHotels';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';
import { MOVED_EVENT } from 'hooks/usePackageMyBookings';

// WHIT-485 round 3: the customer's favourite hotels, shared by every heart on
// the page and the favourites page. Read once per signed-in user; a visitor
// nobody has signed in yet has none, and becomes an anonymous user on their
// first heart.

interface FavouritesState {
  // Whose list this is; null while nobody is signed in.
  uid: string | null;
  hotels: PackageFavouriteHotel[] | null;
  error: string | null;
}

let state: FavouritesState = { uid: null, hotels: null, error: null };
const listeners = new Set<() => void>();
const setState = (next: FavouritesState) => {
  state = next;
  listeners.forEach(listener => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const SERVER_STATE: FavouritesState = { uid: null, hotels: null, error: null };

const ask = async (
  user: User,
  query: string,
  variables?: Record<string, any>,
) => {
  const response = await graphQLFetcher<
    Record<string, PackageFavouriteHotelsResult>
  >({ query, variables, headers: await authHeaders(user) });
  return Object.values(response)[0];
};

// Counts changes started and finished. A read that began before one does not
// answer for the list any more: the change's own answer does.
let changes = 0;

// One read at a time per user; one asked for meanwhile (after a move of
// favourites, say) runs when it finishes, so it sees the latest list.
let reading: string | null = null;
let readAgain = false;
const read = async (user: User): Promise<void> => {
  if (reading === user.uid) {
    readAgain = true;
    return;
  }
  reading = user.uid;
  const seen = changes;
  try {
    const answer = await ask(user, packageFavouriteHotelsQuery);
    if (state.uid !== user.uid || changes !== seen) return;
    setState({
      uid: user.uid,
      hotels: answer?.hotels || [],
      error: answer?.hotels ? null : answer?.code || 'UNKNOWN',
    });
  } catch {
    if (state.uid === user.uid && changes === seen)
      setState({ ...state, error: 'UNKNOWN' });
  } finally {
    reading = null;
    if (readAgain) {
      readAgain = false;
      read(user);
    }
  }
};

// A change taurus-api did not make: back to the list before it, or — when
// the list had not been read yet — the list read now.
const undo = (user: User, before: PackageFavouriteHotel[] | null) => {
  if (before) setState({ ...state, hotels: before });
  else read(user);
};

// Marks or unmarks a hotel: the heart changes at once, and changes back when
// taurus-api refuses or cannot be reached (the code is returned). A visitor
// nobody has signed in becomes an anonymous user first.
const change = async (hotel: PackageFavouriteHotel, favourite: boolean) => {
  let user = auth.currentUser;
  if (!user) {
    try {
      user = (await signInAnonymously(auth)).user;
    } catch {
      return 'UNKNOWN';
    }
  }
  changes += 1;
  const before = state.uid === user.uid ? state.hotels : null;
  setState({
    uid: user.uid,
    hotels: withFavourite(before, hotel, favourite),
    error: null,
  });
  try {
    const answer = await ask(user, packageFavouriteHotelSetQuery, {
      hotelId: hotel.hotelId,
      name: hotel.name,
      favourite,
    });
    if (state.uid !== user.uid) return null;
    if (answer?.hotels) {
      setState({ uid: user.uid, hotels: answer.hotels, error: null });
    }
    if (answer?.status === 200) return null;
    if (!answer?.hotels) undo(user, before);
    return answer?.code || 'UNKNOWN';
  } catch {
    if (state.uid === user.uid) undo(user, before);
    return 'UNKNOWN';
  } finally {
    changes += 1;
  }
};

// Changes run one after another, whichever heart they come from: each starts
// from the list the previous one left, so a failed one cannot put back a list
// that misses another's hotel — nor can two first hearts make two users.
let queue: Promise<unknown> = Promise.resolve();
const queued = (hotel: PackageFavouriteHotel, favourite: boolean) => {
  const run = queue.then(() => change(hotel, favourite));
  queue = run.catch(() => null);
  return run;
};

export const useFavouriteHotels = () => {
  const { authUser, loading } = useAuthContext();
  const current = useSyncExternalStore(
    subscribe,
    () => state,
    () => SERVER_STATE,
  );
  const uid = authUser?.uid || null;

  useEffect(() => {
    if (!PACKAGE_TOURS_IN_HOUSE || loading) return;
    if (!authUser) {
      if (state.uid !== null || state.hotels === null)
        setState({ uid: null, hotels: [], error: null });
      return;
    }
    if (state.uid !== authUser.uid) {
      setState({ uid: authUser.uid, hotels: null, error: null });
      read(authUser);
    }
  }, [authUser, loading]);

  // An anonymous visitor's favourites move to the account they sign in to,
  // with their bookings; the account's list is read again afterwards.
  useEffect(() => {
    if (!authUser) return undefined;
    const reload = () => read(authUser);
    window.addEventListener(MOVED_EVENT, reload);
    return () => window.removeEventListener(MOVED_EVENT, reload);
  }, [authUser]);

  const mine = current.uid === uid;
  return {
    hotels: mine ? current.hotels : null,
    error: mine ? current.error : null,
    setFavourite: queued,
  };
};
